import { BusinessOutlined } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useId } from 'react';
import { CompactEditButton, SectionCard } from '../../../design-system';
import type { CompanyLegalProfile } from '../model/types';
import { CompanyLegalFields, CompanyLegalSummary, type CompanyLegalEditableField } from './CompanyLegalFields';

type CompanyLegalDetailCardProps = {
  canWrite: boolean;
  profile: CompanyLegalProfile;
  dirty: boolean;
  valid: boolean;
  editing: boolean;
  saving: boolean;
  editAriaLabel: string;
  onCancel: () => void;
  onEdit: () => void;
  onSave: () => void;
  onChange: (field: CompanyLegalEditableField, value: string) => void;
};

export function CompanyLegalDetailCard({
  canWrite,
  profile,
  dirty,
  valid,
  editing,
  saving,
  editAriaLabel,
  onCancel,
  onEdit,
  onSave,
  onChange,
}: CompanyLegalDetailCardProps) {
  const headingId = useId();
  return (
    <Box
      component="section"
      data-testid="company-legal-detail-card"
      aria-labelledby={headingId}
    >
      <SectionCard
        title={
          <Typography id={headingId} component="h2" variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessOutlined aria-hidden sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
            기본 정보
          </Typography>
        }
        action={
          canWrite ? (
            editing ? (
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" color="inherit" onClick={onCancel} disabled={saving}>
                  취소
                </Button>
                <Button
                  variant="outlined"
                  onClick={onSave}
                  disabled={!dirty || !valid || saving}
                >
                  {saving ? '저장 중…' : '저장'}
                </Button>
              </Stack>
            ) : (
              <CompactEditButton aria-label={editAriaLabel} onClick={onEdit}>
                수정
              </CompactEditButton>
            )
          ) : undefined
        }
        bodyPadding="standard"
        stickyHeader={editing}
      >
        {editing ? (
          <CompanyLegalFields profile={profile} disabled={saving} readOnly={false} onChange={onChange} />
        ) : (
          <CompanyLegalSummary profile={profile} />
        )}
      </SectionCard>
    </Box>
  );
}
